import React, { useContext, useEffect, useState } from 'react';
import './App.css';

import { Button, Drawer, Layout, Menu, message } from 'antd';
import { Content, Header } from 'antd/es/layout/layout';
import { MenuItemType } from 'antd/es/menu/interface';
import {
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { BarsOutlined, LogoutOutlined } from '@ant-design/icons';

import GraphEditor from './components/GraphEditor';
import GraphsList from './components/GraphsList';
import { AppContext } from './context';
import ProjectsList from './components/ProjectsList';
import ProjectSettings from './components/ProjectSettings';
import { useClient, User } from './client';
import Auth from './components/Auth';
import { AxiosError, CanceledError } from 'axios';
import LoadingPlaceholder from './components/LoadingPlaceholder';
import EndpointList from './components/EndpointList';
import { GoogleOAuthProvider } from '@react-oauth/google';
import DatasetsList from './components/DatasetsList';
import TasksList from './components/TasksList';
import SignUp from './components/SignUp';
import EmailVerification from './components/EmailVerification';

type PageId =
  | 'projects'
  | 'endpoints'
  | 'graphs'
  | 'datasets'
  | 'tasks'
  | 'settings';

type NavMenuItemType = MenuItemType & {
  key: PageId;
};

const LS_PROJECT_ID_KEY = 'projectId';

function App({ loading }: { loading?: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId, setProjectId, projectName, user, setUser } =
    useContext(AppContext);
  const [projectListOpen, setProjectListOpen] = useState(false);
  const client = useClient(projectId);

  const isSignUp = location.pathname === '/sign-up';

  useEffect(() => {
    if (user && isSignUp) {
      navigate('');
    }
  }, [user, isSignUp, navigate]);

  let navPage: string | null = null;
  switch (location.pathname) {
    case '/projects':
    case '/endpoints':
    case '/graphs':
    case '/settings':
    case '/datasets':
    case '/tasks':
      navPage = location.pathname.slice(1);
      break;
  }

  const navMenuItems: NavMenuItemType[] =
    !loading && projectId && user?.verified
      ? [
          ...(user?.is_admin
            ? ([
                {
                  key: 'projects',
                  label: <Link to="/projects">Projects</Link>,
                },
              ] as NavMenuItemType[])
            : []),
          {
            key: 'endpoints',
            label: <Link to="/endpoints">Endpoints</Link>,
          },
          {
            key: 'graphs',
            label: <Link to="/graphs">Graphs</Link>,
          },
          {
            key: 'datasets',
            label: <Link to="/datasets">Datasets</Link>,
          },
          {
            key: 'tasks',
            label: <Link to="/tasks">Tasks</Link>,
          },
          {
            key: 'settings',
            label: <Link to="/settings">Settings</Link>,
          },
        ]
      : [];

  if (loading) {
    return (
      <div className="app">
        <LoadingPlaceholder />
      </div>
    );
  }

  if (!user) {
    return <div className="app">{isSignUp ? <SignUp /> : <Auth />}</div>;
  }

  return (
    <Layout className="app">
      <Header className="app-header">
        {/* {user.is_admin && (
          <Button
            shape="circle"
            icon={<BarsOutlined />}
            size="large"
            className="open-nav-button"
            onClick={() => setProjectListOpen(true)}
          />
        )} */}
        <Link className="app-label" to="/">
          LLM Pipeline Editor
        </Link>
        <Menu
          className="app-nav"
          items={navMenuItems}
          selectedKeys={navPage ? [navPage] : undefined}
          mode="horizontal"
          theme="dark"
        />
        <p className="app-header-project-name">{projectName}</p>
        <p>{user.name}</p>
        <Button
          type="link"
          icon={<LogoutOutlined />}
          onClick={async () => {
            await client.logout();
            setUser(null);
            setProjectId(null);
          }}
        />
      </Header>
      {/* {user.is_admin && (
        <Drawer
          title="Projects"
          open={projectListOpen}
          onClose={() => setProjectListOpen(false)}
          placement="left"
          forceRender
        >
          <ProjectsList />
        </Drawer>
      )} */}
      {user.verified ? (
        <Content className="app-content">
          <Routes>
            <Route index element={<></>} />
            {user?.is_admin && (
              <Route path="/projects" element={<ProjectsList />} />
            )}
            <Route path="/endpoints" element={<EndpointList />} />
            <Route path="/graphs" element={<GraphsList />} />
            <Route path="/graphs/:graphId" element={<GraphEditor />} />
            <Route path="/datasets" element={<DatasetsList />} />
            <Route path="/tasks" element={<TasksList />} />
            <Route path="/settings" element={<ProjectSettings />} />
          </Routes>
        </Content>
      ) : (
        <EmailVerification email={user.email} />
      )}
    </Layout>
  );
}

export default function AppContainer() {
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [fetchingProjects, setFetchingProjects] = useState(false);
  const [fetchingUser, setFetchingUser] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();
  const client = useClient(null);

  useEffect(() => {
    setFetchingUser(true);
    (async () => {
      try {
        const user = await client.getUser();
        setUser(user);
        setFetchingProjects(true);
      } catch (error) {
        if (error instanceof CanceledError) return;
        if (!(error instanceof AxiosError && error.status === 401)) {
          messageApi.error('Error');
          console.log('Error:', error);
        }
      }
      setFetchingUser(false);
    })();
  }, [messageApi, client]);

  useEffect(() => {
    if (projectId === null) return;
    localStorage.setItem(LS_PROJECT_ID_KEY, projectId);
  }, [projectId]);

  useEffect(() => {
    if (!user) return;
    setFetchingProjects(true);
    (async () => {
      try {
        const projects = await client.listProjects();
        setFetchingProjects(false);
        const lastProjectId = localStorage.getItem(LS_PROJECT_ID_KEY);
        if (lastProjectId) {
          const lastProject = projects.find(
            (project) => project.id.toString(10) === lastProjectId
          );
          if (lastProject) {
            setProjectId(lastProjectId);
            setProjectName(lastProject.name);
            return;
          }
        }
        if (projects.length) {
          const project = projects[0];
          const pId = project.id.toString(10);
          setProjectId(pId);
          setProjectName(project.name);
          localStorage.setItem(LS_PROJECT_ID_KEY, pId);
        }
      } catch (error) {
        if (error instanceof CanceledError) return;
        messageApi.error('Error fetching project list');
        console.log('Error fetching project list:', error);
      }
      setFetchingProjects(false);
    })();
  }, [user, messageApi, client]);

  return (
    <AppContext.Provider
      value={{
        projectId,
        setProjectId,
        projectName,
        setProjectName,
        user,
        setUser,
      }}
    >
      {contextHolder}
      <GoogleOAuthProvider clientId="242874759333-lrah3u8d22npv139p3qqh5g6iss3topo.apps.googleusercontent.com">
        <App loading={fetchingUser || fetchingProjects} />
      </GoogleOAuthProvider>
    </AppContext.Provider>
  );
}
